import isEqual from 'lodash/isEqual';
import type {PosterObject} from '@PosterWhiteboard/poster/poster.types';

/**
 * Structural sharing for the Redux poster mirror.
 *
 * Every commit serializes the whole poster (`Poster.toObject()`), which hands every page, item
 * and audio clip a brand-new object even when nothing about it changed. Redux, reselect and React
 * all compare by identity, so an unshared publish reads as "everything changed": every selector
 * recomputes, every memoized component re-renders and every thumbnail re-hashes, on every commit.
 *
 * This module runs between serialization and dispatch. For each item, audio clip and page it asks
 * "is this deeply equal to what the store already holds?" and, if so, puts the store's object back
 * into the fresh poster. After that, identity means "unchanged since the last commit" all the way
 * down the tree, which is what lets the timeline bars, the thumbnail cache and the per-item
 * selectors skip work.
 *
 * Rules the implementation relies on:
 * - `previous` is the object already in the store. Immer froze it; it is only ever read.
 * - `next` is the fresh serialization nobody else has seen yet, so it may be patched in place.
 * - Whole-page reuse is decided after item and audio reuse, so a page whose members all matched
 *   compares by identity for most of its weight and the deep comparison stays cheap.
 * - Cost is one deep comparison of the poster per commit, roughly the price of the serialization
 *   it follows. It never runs on playback ticks or drag ticks (see `posterSession`).
 */

/**
 * Returns `next` with every page, item and audio clip that is deeply equal to its counterpart in
 * `previous` replaced by that counterpart. On the first publish there is nothing to share, so
 * `next` is returned as is.
 */
export function stabilizePosterObject(previous: PosterObject | undefined, next: PosterObject): PosterObject {
  if (!previous) {
    return next;
  }

  for (const [pageId, page] of Object.entries(next.pages.pagesHashMap)) {
    const previousPage = previous.pages.pagesHashMap[pageId];
    if (previousPage === undefined) {
      continue;
    }
    shareUnchangedEntries(previousPage.items.itemsHashMap, page.items.itemsHashMap);
    shareUnchangedEntries(previousPage.audioClips.audioItemsHashMap, page.audioClips.audioItemsHashMap);
    if (isEqual(previousPage, page)) {
      next.pages.pagesHashMap[pageId] = previousPage;
    }
  }
  return next;
}

/** Replaces entries of `next` (items or audio clips, keyed by uid) with the deeply equal entry from `previous`. */
function shareUnchangedEntries<T>(previous: Record<string, T>, next: Record<string, T>): void {
  for (const [uid, entry] of Object.entries(next)) {
    const previousEntry = previous[uid];
    if (previousEntry !== undefined && isEqual(previousEntry, entry)) {
      next[uid] = previousEntry;
    }
  }
}
